import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { cloneDeep, isEqual } from 'lodash';

import { api } from '../../../../services/api';
import { session } from '../../../../services/session';
import { authService } from '../../../../services/auth';
import { sortAlphabetical } from '../../../../utils/utils';

import OrgItem from './org-item';

import './org-select.scss';

class OrgSelect extends Component {
  constructor(props) {
    super(props);

    this.Api = api;
    this.AuthService = authService;

    this.organizations = []; // All orgs from API
    this.orgList = []; // All orgs except hidden ones
    this.merchants = {}; // Merchants keyed on merch_id
    this.publishers = {}; // Publishers keyed on pub_id
    this.adminOrg = { short_name: 'Admin' };

    this.dropdownNode = null;
    this.loadingOrgs = false;

    this.state = {
      organizations: this.organizations,
      orgList: this.orgList,
      merchants: this.merchants,
      publishers: this.publishers,
      searchTerm: '',
      listOpen: false,
    };

    /*
     * Can't use componentDidUpdate's prevProps since they get mutated (Session is a singleton).
     * instead, we keep track of the last accessList ourselves and compare in componentDidUpdate
     */
    this.accessList = null;
  }

  componentDidMount() {
    window.addEventListener('click', this.handleClick.bind(this), false);

    this.loadOrgs();
    this.accessList = cloneDeep(this.props.currentUser.accessList);
  }

  componentDidUpdate() {
    if (!isEqual(this.accessList, this.props.currentUser.accessList)) {
      this.loadOrgs();
    }
    this.accessList = cloneDeep(this.props.currentUser.accessList);

    const { pathname } = window.location;
    const orgId = parseInt(pathname.split('/')[3]);
    const orgRoute = pathname.split('/')[2];

    // Check if the user has updated either the org ID or user role in the url
    if (
      (orgId && orgId !== this.props.currentUser.orgId) ||
      (orgRoute && orgRoute !== this.userRole())
    ) {
      this.manualOrgChange(orgId, orgRoute);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.handleClick.bind(this), false);
  }

  /* Event handlers */
  handleClick(event) {
    if (this.dropdownNode && !this.dropdownNode.contains(event.target)) {
      this.dropdownNode.classList.remove('open');
    }
  }

  handleSearchChange(event) {
    this.setState({ searchTerm: event.target.value });
    this.filterOrgsByName();
  }

  toggleDropdown() {
    this.dropdownNode.classList.toggle('open');
  }

  /* Main functions */
  loadOrgs() {
    const orgPromises = this.AuthService.buildOrgRequests(this.props.currentUser);
    this.loadingOrgs = true;

    return Promise.all(orgPromises)
      .then(responses => {
        responses.forEach(resp => {
          this.organizations = this.organizations.concat(resp.data.data);
        });
      })
      .then(() => {
        this.loadingOrgs = false;
        this.sortOrgs();
        this.orgList = this.filterOrgs();

        const orgDicts = this.AuthService.buildOrgDicts(this.organizations);
        this.merchants = orgDicts.merchants;
        this.publishers = orgDicts.publishers;

        const orgId = parseInt(window.location.pathname.split('/')[3]);
        if (orgId && this.merchants[orgId] && session.userRole === 'merchants') {
          session.update({
            merchPricingModel: this.merchants[orgId].pricing_model
              ? this.merchants[orgId].pricing_model
              : null,
          });
        }
      })
      .then(() => {
        this.setState(
          {
            organizations: this.organizations,
            orgList: this.orgList,
            merchants: this.merchants,
            publishers: this.publishers,
          },
          () => this.props.update(),
        );
      });
  }

  switchOrg(org) {
    // Switch user to admin/publisher/merchant access if available
    this.setState({ searchTerm: '' });

    this.filterOrgsByName();
    this.toggleDropdown();

    if (this.AuthService.updateSession(org)) {
      if (session.userRole === 'merchants' && org.pricing_model) {
        session.update({ merchPricingModel: org.pricing_model });
      }
      this.props.history.push('/v2'); // Clear query string as well
    }
  }

  userRole() {
    switch (this.props.currentUser.userRole) {
      case 'publishers':
        return 'publisher';
      case 'merchants':
        return 'merchant';
      default:
        return 'admin';
    }
  }

  manualOrgChange(orgId, orgRoute) {
    let orgs = {};

    switch (orgRoute) {
      case 'publisher':
        orgs = this.state.publishers;
        break;
      case 'merchant':
        orgs = this.state.merchants;
        break;
      default:
        orgs = {};
    }

    const newOrg = orgs[orgId];

    // If the user has access to the new org, manually update the session.
    if (newOrg) {
      // Switch user to admin/publisher/merchant access if available
      this.setState({ searchTerm: '' });
      this.AuthService.updateSession(newOrg);
    } else if (!this.loadingOrgs) {
      window.history.back();
    }
  }

  /* Helper functions */
  filterOrgs() {
    // Filter out hidden orgs from this.orgList
    return this.organizations.filter(org => !this.orgIsHidden(org));
  }

  filterOrgsByName() {
    const orgList = this.state.organizations.filter(org => {
      const name = org.short_name || '';
      return (
        !this.orgIsHidden(org) && name.toLowerCase().includes(this.state.searchTerm.toLowerCase())
      );
    });
    this.setState({ orgList });
  }

  sortOrgs() {
    sortAlphabetical(this.organizations, 'short_name');
  }

  /* Getter functions */
  getDisplayName() {
    const user = this.props.currentUser;
    return user.allSites
      ? user.isAdmin
        ? user.allPubs
          ? 'All Publishers'
          : 'All Merchants'
        : 'All Sites'
      : this.getOrgName(user.orgId, user.userRole, user.isAdmin);
  }

  getOrgInfo(org) {
    let orgType;
    let orgId;

    if (org.pub_id) {
      orgId = org.pub_id;
      orgType = 'publishers';
    } else if (org.merch_id) {
      orgId = org.merch_id;
      orgType = 'merchants';
    } else {
      orgId = org.orgId || 0;
      orgType = org.type;
    }

    return [orgType, orgId];
  }

  getLabel(org) {
    const [orgType, orgId] = this.getOrgInfo(org);
    return this.getOrgName(orgId, orgType);
  }

  getOrgName(orgId, orgType, isAdmin) {
    let name;
    try {
      name = this.state[orgType][orgId].short_name;
    } catch (e) {
      name = isAdmin ? 'Admin' : this.props.currentUser.userRole;
    }
    return name;
  }

  getOrgPrefix(org) {
    const [orgType] = this.getOrgInfo(org);
    let orgPrefix = '';

    if (orgType === 'publishers') {
      orgPrefix = 'P';
    } else if (orgType === 'merchants') {
      orgPrefix = 'R'; // Retailer
    } else {
      orgPrefix = org.label || '';
    }
    return orgPrefix;
  }

  orgIsHidden(org) {
    // Hide pilot orgs and inactive merchants from dropdown
    if (org.pub_id) {
      return org.is_pilot && !org.show_in_dashboard;
    }
    // If show_in_dashboard is enabled, do not hide org
    return (!org.is_active || org.is_pilot) && !org.show_in_dashboard;
  }

  showAdmin() {
    return !this.state.searchTerm || 'admin'.includes(this.state.searchTerm.toLowerCase());
  }

  getAllAdminOrgs() {
    return this.state.orgList.map(org => {
      const newOrg = cloneDeep(org);
      if (org.merch_id) {
        newOrg.orgId = org.merch_id;
        newOrg.type = 'merchants';
      } else if (org.pub_id) {
        newOrg.orgId = org.pub_id;
        newOrg.type = 'publishers';
      }
      return newOrg;
    });
  }

  toggleAllSites(admin = false, allPubs = true, org = {}) {
    this.filterOrgsByName();
    this.toggleDropdown();
    const update = { allSites: true };

    if (admin) {
      update.allPubs = allPubs;
      update.allAdminOrgs = this.getAllAdminOrgs();
    }

    session.update({ ...update });

    if (org.merch_id || org.pub_id) {
      // For 'All Publishers' and 'All Merchants', need a more complete update
      this.AuthService.updateSession(org, true);
    }

    this.props.history.push('/v2');
  }

  render() {
    let multiAccount;
    // Identify a pub and merch org to change default /v2 navigation after allSite change
    const firstPub = this.state.orgList.find(org => org.pub_id);
    const firstMerch = this.state.orgList.find(org => org.merch_id);

    if (!this.props.currentUser.isAdmin && !this.props.currentUser.accessList.length) {
      return null;
    }

    if (
      [2009, 2034, 2124, 2125, 2126].includes(session.userId) ||
      (session.userRole === 'publishers' && !session.isAdmin && session.publisher_type !== 2)
    ) {
      multiAccount = (
        <OrgItem
          isAdmin={this.props.currentUser.isAdmin}
          org={{}}
          label="All Sites"
          switchOrg={() => this.toggleAllSites()}
        />
      );
    } else if (session.isAdmin) {
      multiAccount = (
        <>
          <OrgItem
            isAdmin={this.props.currentUser.isAdmin}
            org={{}}
            label="All Publishers"
            switchOrg={() => this.toggleAllSites(true, true, firstPub)}
          />
          <OrgItem
            isAdmin={this.props.currentUser.isAdmin}
            org={{}}
            label="All Merchants"
            switchOrg={() => this.toggleAllSites(true, false, firstMerch)}
          />
        </>
      );
    }

    const logoutEl = this.props.isMobile ? (
      <li className="logout" onClick={() => authService.logout(true)}>
        Sign out
      </li>
    ) : null;
    const dropdown = (
      <ul className="dropdown-menu">
        {multiAccount}
        {this.state.orgList.map((org, index) => {
          return (
            <OrgItem
              key={index}
              isAdmin={this.props.currentUser.isAdmin}
              org={org}
              label={this.getLabel.call(this, org)}
              switchOrg={this.switchOrg.bind(this)}
            ></OrgItem>
          );
        })}
        {logoutEl}
      </ul>
    );

    const adminDropdown = (
      <ul className="dropdown-menu">
        <li className="search-input">
          <span className="narrativ-icon search"></span>
          <form autoComplete="off">
            <input
              type="text"
              placeholder="Search"
              value={this.state.searchTerm}
              onChange={this.handleSearchChange.bind(this)}
              autoComplete="new-password"
            />
          </form>
        </li>

        <OrgItem
          className="admin-label"
          isAdmin={this.props.currentUser.isAdmin}
          org={this.adminOrg}
          label={this.adminOrg.short_name}
          switchOrg={this.switchOrg.bind(this)}
        ></OrgItem>
        {multiAccount}

        {this.state.orgList.map((org, index) => {
          return (
            <OrgItem
              key={index}
              isAdmin={this.props.currentUser.isAdmin}
              org={org}
              label={this.getLabel.call(this, org)}
              orgPrefix={this.getOrgPrefix.call(this, org)}
              switchOrg={this.switchOrg.bind(this)}
            ></OrgItem>
          );
        })}
      </ul>
    );

    const orgDropdown = this.props.currentUser.isAdmin ? adminDropdown : dropdown;

    return (
      <div ref={node => (this.dropdownNode = node)} className="admin-org-dropdown">
        <div tabIndex={0} className="dropdown-toggle" onClick={this.toggleDropdown.bind(this)}>
          <span>{this.getDisplayName()}</span>
          <span className="narrativ-icon down"></span>
        </div>

        {orgDropdown}
      </div>
    );
  }
}

export default withRouter(OrgSelect);
